import { ASSETS_BASE_URL } from "../api/axios";

export const getValueByPath = (object, path) => {
  const properties = path.split(".");
  let value = object;
  for (let i = 0; i < properties.length; i++) {
    value = value[properties[i]];
    if (value === undefined) {
      return "";
    }
  }
  return value;
};

export const formatDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    const date = new Date(dateStr);
    const options = {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
    };
    let formattedDate = new Intl.DateTimeFormat("en-US", options)
      .format(date)
      .replace(/,/g, "");
    formattedDate = formattedDate.replace(/(\d{1,2}:\d{2}:\d{2})/, "at $1");
    formattedDate = formattedDate.replace(/(AM|PM)/, (match) => match.toLowerCase());
    return formattedDate;
  } catch (error) {
    console.error(error);
    return "";
  }
};

export const formatDateShort = (dateStr) => {
  if (!dateStr) return "";
  try {
    const date = new Date(dateStr);
    const options = {
      weekday: "short",
      month: "short",
      day: "2-digit",
      year: "numeric",
    };
    let formattedDate = new Intl.DateTimeFormat("en-US", options)
      .format(date)
      .replace(/,/g, "");
    return formattedDate;
  } catch (error) {
    console.error(error);
    return "";
  }
};

export const getExpTimeSeconds = (dateTimeString) => {
  const currentTime = new Date();
  const givenDateTime = new Date(dateTimeString);
  if (isNaN(givenDateTime)) {
    return 10;
  }
  const seconds = Math.floor((givenDateTime - currentTime) / 1000);
  if (seconds > 10) {
    return seconds - 10;
  }
  return 10;
};

export const findIntersection = (A = [], B = []) => {
  return A.filter((x) => B.includes(x)).length > 0;
};

export const sanitizeFilename = (text) => {
  const fileName = text
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x1F\x7F-\x9F]/g, "")
    .replace(/[<>:"\\/|?*]/g, "_")
    .replace(/\s+/g, "-")
    .toLowerCase();
  return fileName || "my-file";
};

export const isAbsoluteURL = (url) => {
  try {
    new URL(url);
    return true;
  } catch (e) {
    return false;
  }
};

export const getAssetsURL = (url) => {
  if (!isAbsoluteURL(url)) {
    return `${ASSETS_BASE_URL}/${url}`;
  }
  return url;
};

// "Today, 9:14 PM", "Yesterday, 9:14 PM", "Monday, 2:36 PM" (within the last week),
// then "Sep 12, 2:36 PM" (this year) or "Sep 12, 2025, 2:36 PM".
export const formatRelativeDateTime = (dateStr, now = new Date()) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return "";

  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const daysAgo = Math.round((startOfDay(now) - startOfDay(date)) / 86400000);
  const time = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

  let day;
  if (daysAgo === 0) day = "Today";
  else if (daysAgo === 1) day = "Yesterday";
  else if (daysAgo > 1 && daysAgo < 7) day = date.toLocaleDateString("en-US", { weekday: "long" });
  else
    day = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      ...(date.getFullYear() !== now.getFullYear() && { year: "numeric" }),
    });

  return `${day}, ${time}`;
};

// "Yesterday, 9:14 PM By You" / "Monday, 2:36 PM By Cassidy Milfort"
export const formatEditedBy = (dateStr, author, currentUserName) => {
  const when = formatRelativeDateTime(dateStr);
  const name = (author || "").trim();
  if (!name) return when;
  const isMe =
    !!currentUserName && name.toLowerCase() === currentUserName.trim().toLowerCase();
  return `${when} By ${isMe ? "You" : name}`;
};

// Saves a { base64, content_type, filename } API response as a file download.
export const downloadBase64File = ({ base64, content_type, filename }) => {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const blob = new Blob([bytes], { type: content_type });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

// "Today at 1pm by Tom Wheeler", "Yesterday at 2:30pm", "Monday at 9am", "Sep 12 at 4pm".
export const formatAtBy = (dateStr, by, now = new Date()) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return "";

  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const daysAgo = Math.round((startOfDay(now) - startOfDay(date)) / 86400000);

  let day;
  if (daysAgo === 0) day = "Today";
  else if (daysAgo === 1) day = "Yesterday";
  else if (daysAgo > 1 && daysAgo < 7) day = date.toLocaleDateString("en-US", { weekday: "long" });
  else
    day = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      ...(date.getFullYear() !== now.getFullYear() && { year: "numeric" }),
    });

  const hours = date.getHours();
  const minutes = date.getMinutes();
  const time = `${hours % 12 || 12}${minutes ? `:${String(minutes).padStart(2, "0")}` : ""}${
    hours < 12 ? "am" : "pm"
  }`;

  const name = (by || "").trim();
  return `${day} at ${time}${name ? ` by ${name}` : ""}`;
};
